// src/components/admin-tools/tools-helpers.ts
//
// Helpers PUROS da tela de Tools (admin) — wireframe §13 (RAIOS-UX-SIDEBAR-SPEC).
// Sem React/fetch aqui pra serem testáveis unitariamente.

import type { AdminTool } from '@/lib/api';

/** tools.status existe no backend (SELECT da rota admin/tools) mas ainda não
 *  está no AdminTool do api.ts (não podemos tocar api.ts nesta frente). */
export type ToolRow = AdminTool & { status?: string };

/** Atributos togglavéis no card — ordem e labels do wireframe (§13):
 *  core · admin-only · mcp · companion-only. */
export const TOOL_ATTRIBUTES = [
  { key: 'is_core', label: 'core' },
  { key: 'requires_admin', label: 'admin-only' },
  { key: 'mcp_exposed', label: 'mcp' },
  { key: 'is_companion_only', label: 'companion-only' },
] as const;

export type ToolAttributeKey = (typeof TOOL_ATTRIBUTES)[number]['key'];

/** Categorias aceitas pelo create_tool no backend (TOOL_CATEGORIES em
 *  create-tool.ts) — também as 4 do wireframe. A tabela tools tem ainda
 *  'storage' e 'mcp' em dados legados; o filtro da lista deriva do dado real. */
export const CREATABLE_CATEGORIES = ['dispatch', 'query', 'write', 'external'] as const;

export type StatusFilter = 'all' | 'active' | 'inactive';
export type AttributeFilter = 'all' | ToolAttributeKey;

/** Mesmo regex do backend (create-tool.ts SLUG_RE). */
export const SLUG_RE = /^[a-z][a-z0-9_]*$/;

/** Deriva slug snake_case a partir do nome humano (ex.: "Enviar fatura" →
 *  "enviar_fatura"). Remove acentos, troca não-alfanumérico por "_", colapsa
 *  e garante começo com letra. */
export function deriveSlug(name: string): string {
  const base = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // acentos (combining marks pós-NFD)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  // slug precisa começar com letra (SLUG_RE) — descarta dígitos/underscores à frente
  return base.replace(/^[^a-z]+/, '');
}

export interface ToolFilters {
  search: string;
  category: string; // 'all' | categoria real
  attribute: AttributeFilter;
  status: StatusFilter;
}

export function filterTools(tools: ToolRow[], f: ToolFilters): ToolRow[] {
  const q = f.search.trim().toLowerCase();
  return tools.filter((t) => {
    if (f.category !== 'all' && t.category !== f.category) return false;
    if (f.attribute !== 'all' && !t[f.attribute]) return false;
    if (f.status === 'active' && !t.is_active) return false;
    if (f.status === 'inactive' && t.is_active) return false;
    if (
      q &&
      !t.slug.toLowerCase().includes(q) &&
      !(t.name ?? '').toLowerCase().includes(q) &&
      !(t.description ?? '').toLowerCase().includes(q)
    )
      return false;
    return true;
  });
}

export interface ToolMetrics {
  total: number;
  core: number;
  adminOnly: number;
  mcp: number;
  companionOnly: number;
}

/** Métricas do topo — total · core · admin-only · MCP · companion-only (TOOL-02). */
export function computeToolMetrics(tools: ToolRow[]): ToolMetrics {
  return {
    total: tools.length,
    core: tools.filter((t) => t.is_core).length,
    adminOnly: tools.filter((t) => t.requires_admin).length,
    mcp: tools.filter((t) => t.mcp_exposed).length,
    companionOnly: tools.filter((t) => t.is_companion_only).length,
  };
}

/** Opções do filtro de categoria: as 4 do wireframe + qualquer categoria real
 *  presente nos dados (ex.: storage/mcp legadas) — o dado real vence. */
export function categoryOptions(tools: ToolRow[]): string[] {
  const set = new Set<string>(CREATABLE_CATEGORIES);
  for (const t of tools) if (t.category) set.add(t.category);
  return [...set];
}
